/**
 * Browser notifications when nothing else is set up (R-HL-2). Two layers:
 *   - "Turn on alerts" subscribes this browser to web push when the hub has a notify module
 *     (enablePush): the browser becomes a push channel, and the service worker shows alerts with
 *     every Orgus tab closed. That is what reaches a person who is not looking.
 *   - Until then, and on a hub without a notify module, this in-tab fallback: with no enabled
 *     channel, an approval, a reminder or a blocked mission still reaches the person through this
 *     browser while a console tab is open anywhere.
 * Pure, so it is unit tested; NotifyFallback.tsx is the thin component around it.
 *
 *   source     state.ext.notify.recent at high or critical urgency; without a notify module,
 *              state.ext.approvals pending and blocking, with the same texts built here
 *   baseline   everything present when the tab loads counts as seen (opening the console is seeing it)
 *   dedupe     the notification id (each one the hub sent shows once, across tabs, through a ring
 *              of the last 200 in localStorage); for alerts built here, approval:<id>. The OS
 *              `tag` is `${ref.type}:${ref.id}`, so a newer alert about the same thing replaces
 *              the older one on screen instead of stacking.
 *   visible    a toast in the tab, not an OS notification
 */
import { approvalOutcomeOf, urgencyRank } from '@orgus/protocol';
import type { ApprovalRequest, ApprovalsState, Notification, NotifyState, Urgency } from '@orgus/protocol';

export const SHOWN_KEY = 'orgus.notify.shown';
export const DISMISS_KEY = 'orgus.notify.fallback';
export const SHOWN_MAX = 200;
export const DISMISS_FOR_MS = 14 * 24 * 60 * 60_000;

export interface FallbackAlert {
  /** Dedupe key: the notification id (or approval:<id> for alerts built here). */
  key: string;
  /** The OS tag: a newer alert about the same thing replaces the older one on screen. */
  tag: string;
  title: string;
  body: string;
  /** Deep link in the console. */
  url: string;
  urgency: Urgency;
  /** Stays on screen until the person acts (critical, or an approval). */
  requireInteraction: boolean;
  at: number;
}

/** Active when the browser can notify and nothing else reaches the person: no notify module, or no enabled channel. */
export function fallbackActive(notify: Pick<NotifyState, 'channels'> | null | undefined, supported: boolean): boolean {
  if (!supported) return false;
  if (!notify) return true;
  return !notify.channels.some((c) => c.enabled);
}

const sentence = (t: string): string => {
  const s = t.trim();
  return !s || /[.!?]$/.test(s) ? s : `${s}.`;
};

/** The tag a notification is shown under. */
export function tagOf(n: Pick<Notification, 'id' | 'ref'>): string {
  return n.ref ? `${n.ref.type}:${n.ref.id}` : n.id;
}

export function alertOfNotification(n: Notification, clock: (ts: number) => string): FallbackAlert {
  return {
    // Each notification the hub sends shows once: a session waiting again, or a machine dropping
    // offline twice, is a new notification with the same ref, and must show again.
    key: n.id,
    tag: tagOf(n),
    title: n.title,
    body: n.expiresAt ? `${sentence(n.body)} Expires ${clock(n.expiresAt)}.` : n.body,
    url: n.url,
    urgency: n.urgency,
    requireInteraction: n.urgency === 'critical' || n.ref?.type === 'approval',
    at: n.at,
  };
}

/** The alerts built locally from the approvals module, for a hub without a notify module. */
export function alertsOfApprovals(s: Partial<ApprovalsState> | null | undefined, clock: (ts: number) => string): FallbackAlert[] {
  const out: FallbackAlert[] = [];
  for (const a of s?.pending ?? []) {
    out.push({
      key: `approval:${a.id}`,
      tag: `approval:${a.id}`,
      title: 'Approval needed',
      body: `${sentence(a.title)} Expires ${clock(a.expiresAt)}.`,
      url: `/inbox?approval=${encodeURIComponent(a.id)}`,
      urgency: 'high',
      requireInteraction: true,
      at: a.createdAt,
    });
  }
  for (const a of s?.blocking ?? []) {
    const outcome = approvalOutcomeOf(a);
    if (!outcome || a.dismissedAt || a.laterWorkAt) continue;
    out.push(blockedAlert(a, outcome));
  }
  return out;
}

function blockedAlert(a: ApprovalRequest, outcome: 'expired' | 'denied'): FallbackAlert {
  const what = outcome === 'expired' ? 'approval expired' : 'approval denied';
  return {
    key: `approval.blocked:${a.id}`,
    tag: `approval.blocked:${a.id}`,
    title: `${a.missionId ? 'Mission' : 'Session'} blocked: ${what}`,
    body: `${sentence(a.title)} Approve and resume from Orgus.`,
    url: a.missionId ? `/missions/${encodeURIComponent(a.missionId)}?approval=${encodeURIComponent(a.id)}` : `/sessions?session=${encodeURIComponent(a.sessionId)}&approval=${encodeURIComponent(a.id)}`,
    urgency: 'high',
    requireInteraction: false,
    at: a.decidedAt ?? a.createdAt,
  };
}

/** Every alert the sources hold now, at high urgency or above, newest last. */
export function candidateAlerts(notify: Pick<NotifyState, 'recent'> | null | undefined, approvals: Partial<ApprovalsState> | null | undefined, clock: (ts: number) => string): FallbackAlert[] {
  const list = notify ? notify.recent.filter((n) => urgencyRank(n.urgency) >= urgencyRank('high')).map((n) => alertOfNotification(n, clock)) : alertsOfApprovals(approvals, clock);
  const seen = new Set<string>();
  const out: FallbackAlert[] = [];
  for (const a of list) {
    if (seen.has(a.key)) continue;
    seen.add(a.key);
    out.push(a);
  }
  return out.sort((x, y) => x.at - y.at);
}

/** The alerts to raise: not in the baseline and not shown before (in this or another tab). */
export function newAlerts(candidates: readonly FallbackAlert[], seen: ReadonlySet<string>, shown: readonly string[]): FallbackAlert[] {
  const ring = new Set(shown);
  return candidates.filter((a) => !seen.has(a.key) && !ring.has(a.key));
}

/**
 * One pass of the watcher: nothing until a source has loaded (the approvals slice can arrive
 * after the first paint, and its contents are not news); then the first pass takes the baseline;
 * later passes return what is new while the fallback is active, and remember everything seen.
 */
export function fallbackStep(
  seen: ReadonlySet<string> | null,
  candidates: readonly FallbackAlert[],
  p: { loaded: boolean; active: boolean; shown: readonly string[] },
): { seen: ReadonlySet<string> | null; fresh: FallbackAlert[] } {
  if (!p.loaded) return { seen, fresh: [] };
  if (!seen) return { seen: new Set(candidates.map((c) => c.key)), fresh: [] };
  const fresh = p.active ? newAlerts(candidates, seen, p.shown) : [];
  const next = new Set(seen);
  for (const c of candidates) next.add(c.key);
  return { seen: next, fresh };
}

/** Add tags to the shown ring, keeping the last SHOWN_MAX. */
export function remember(shown: readonly string[], keys: readonly string[]): string[] {
  const next = [...shown.filter((k) => !keys.includes(k)), ...keys];
  return next.slice(-SHOWN_MAX);
}

/** How a new alert is shown: an OS notification while the tab is hidden or unfocused, else a toast. */
export function deliveryOf(p: { visible: boolean; focused: boolean; permission: NotificationPermission }): 'toast' | 'os' {
  return p.visible && p.focused ? 'toast' : p.permission === 'granted' ? 'os' : 'toast';
}

export type BannerState = 'ask' | 'denied' | 'none';

/**
 * The banner: ask to turn alerts on (permission not asked yet, or granted but this browser could
 * still get push and has not: `canPush`), explain a block once (denied), or nothing.
 */
export function bannerState(p: { active: boolean; permission: NotificationPermission | 'unsupported'; dismissedAt: number | null; deniedSeen: boolean; now: number; canPush?: boolean }): BannerState {
  if (!p.active || p.permission === 'unsupported') return 'none';
  if (p.permission === 'granted' && !p.canPush) return 'none';
  if (p.dismissedAt != null && p.now - p.dismissedAt < DISMISS_FOR_MS) return 'none';
  if (p.permission === 'denied') return p.deniedSeen ? 'none' : 'denied';
  return 'ask';
}

/** "(2) Inbox · Orgus" for 2 waiting or blocked; the plain title at zero. */
export function titleWithCount(title: string, count: number): string {
  const base = title.replace(/^\(\d+\)\s+/, '');
  return count > 0 ? `(${count}) ${base}` : base;
}

/** Read the shown ring and the banner record; safe without storage. */
export function readStore(storage: Pick<Storage, 'getItem'> | null): { shown: string[]; dismissedAt: number | null; deniedSeen: boolean } {
  const parse = (k: string): unknown => {
    try {
      const v = storage?.getItem(k);
      return v ? JSON.parse(v) : null;
    } catch {
      return null;
    }
  };
  const shown = parse(SHOWN_KEY);
  const d = parse(DISMISS_KEY) as { dismissedAt?: unknown; deniedSeen?: unknown } | null;
  return {
    shown: Array.isArray(shown) ? shown.filter((x): x is string => typeof x === 'string') : [],
    dismissedAt: d && typeof d.dismissedAt === 'number' ? d.dismissedAt : null,
    deniedSeen: !!d?.deniedSeen,
  };
}
